import React, { useState, useEffect } from 'react';
import { Button } from '@pagespeedify/ui';
import { HelpCircle, X } from 'lucide-react';
import { useTour, TourPage } from '../../context/TourContext';
import { useLocation } from 'react-router-dom';

const TourTriggerButton: React.FC = () => {
  const { startPageTour, resetPageTour } = useTour();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState<TourPage | null>(null);

  // Check if button has been permanently hidden
  useEffect(() => {
    const isHidden = localStorage.getItem('tour-trigger-hidden') === 'true';
    setIsVisible(!isHidden);
  }, []);

  // Determine current page for tour
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/user-dashboard')) {
      setCurrentPage('dashboard');
    } else if (path.includes('/reports')) {
      setCurrentPage('reports');
    } else if (path.includes('/ai-solutions')) {
      setCurrentPage('ai-solutions');
    } else {
      setCurrentPage(null);
    }
  }, [location]);

  const handleStartTour = () => {
    if (currentPage) {
      // Reset tour state first to ensure it can be run again
      resetPageTour(currentPage);
      // Small delay to ensure state is reset, then start tour
      setTimeout(() => {
        startPageTour(currentPage);
      }, 100);
    }
  };

  const handleHidePermanently = () => {
    localStorage.setItem('tour-trigger-hidden', 'true');
    setIsVisible(false);
  };

  // Don't show button if hidden or no valid page
  if (!isVisible || !currentPage) {
    return null;
  }

  const pageName = currentPage === 'dashboard' ? 'Dashboard' 
                  : currentPage === 'reports' ? 'Reports' 
                  : 'AI Solutions';

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div className="relative group">
        {/* Main Tour Button */}
        <Button
          onClick={handleStartTour}
          className="rounded-full w-14 h-14 p-0 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 border-2 border-blue-500"
          title={`Start ${pageName} Tour`}
        >
          <HelpCircle className="w-7 h-7" />
        </Button>

        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
          Start {pageName} Tour
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>

        {/* Hide Button */}
        <Button
          onClick={handleHidePermanently}
          variant="ghost"
          size="sm"
          className="absolute -top-1 -right-1 w-6 h-6 p-0 rounded-full bg-red-500 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-red-400"
          title="Hide Tour Button Permanently"
        >
          <X className="w-3 h-3" />
        </Button>

        {/* Instructions text */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-16 px-2 py-1 bg-blue-900 text-white text-xs rounded opacity-0 group-hover:opacity-90 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
          Tour Guide
        </div>
      </div>
    </div>
  );
};

export default TourTriggerButton;
